/**
 * Reviewing manual orders taken offline: validate, resolve numbering, submit, discard.
 *
 * The largest of the three review workflows. A reviewer here does work rather
 * than making a single choice, so this owns more per-order state than the other
 * two: the validation result, whether possible duplicates were acknowledged, and
 * any order-number collision plus the number typed to replace it.
 *
 * Loading stays coordinated across all three workflows, by owner decision, so
 * this exposes `fetchItems` for the screen's single loader rather than loading
 * on its own.
 *
 * See TCA-26081204.
 */
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  completeLocalReconciliation,
  createOrder,
  discardLocalReconciliationOrder,
  getLocalReconciliationQueue,
  validateManualOrder,
} from '@/api/orders';
import { useReviewActions } from './useReviewActions';

const validationPayload = (order, options = {}) => {
  const payload = {
    delivery_date: order.delivery_date || '',
    recipient_name: order.recipient_name || '',
    recipient_street_address: order.recipient_street_address || '',
    recipient_street_address_2: order.recipient_street_address_2 || '',
    recipient_city: order.recipient_city || '',
    recipient_state: order.recipient_state || '',
    recipient_zip_code: order.recipient_zip_code || '',
    recipient_phone: order.recipient_phone || '',
    sender_name: order.sender_name || '',
    sender_street_address: order.sender_street_address || '',
    sender_street_address_2: order.sender_street_address_2 || '',
    sender_city: order.sender_city || '',
    sender_state: order.sender_state || '',
    sender_zip_code: order.sender_zip_code || '',
    sender_phone: order.sender_phone || '',
    order_number: order.order_number || '',
    network_source: order.network_source || '',
    order_accepted_date: order.order_accepted_date || '',
    card_message: order.card_message || '',
    special_instructions: order.special_instructions || '',
    product_description: order.product_description || '',
    size: order.size || '',
    delivery_time_requirement: order.delivery_time_requirement || '',
    total_price: order.total_price ?? '',
  };
  if (options.internalOrderNumber === null) return payload;
  payload.internal_order_number = (
    options.internalOrderNumber
    || order.internal_order_number
    || ''
  );
  return payload;
};

export function useOfflineOrderReview({ reload, isAdmin, owner }) {
  const act = useReviewActions(owner, reload);
  const [items, setItems] = useState([]);
  const [validatingId, setValidatingId] = useState(null);
  const [submittingId, setSubmittingId] = useState(null);
  const [discardingId, setDiscardingId] = useState(null);
  const [validations, setValidations] = useState({});
  const [numberConflicts, setNumberConflicts] = useState({});
  const [manualNumbers, setManualNumbers] = useState({});
  const [duplicateAcknowledgments, setDuplicateAcknowledgments] = useState({});
  const [completed, setCompleted] = useState([]);
  const validatedInputs = useRef(new Map());

  // Only admins have a manual order queue to review, so a non-admin loads an
  // empty one rather than being refused by the backend.
  const fetchItems = read => (isAdmin ? read.local(getLocalReconciliationQueue) : Promise.resolve([]));

  /**
   * Drop everything remembered about one order.
   *
   * Submitting and discarding both end an order's review, and both previously
   * cleared these four maps with the same twenty lines written twice.
   */
  const forgetOrder = (orderId) => {
    const without = (previous) => {
      const next = { ...previous };
      delete next[orderId];
      return next;
    };
    setValidations(without);
    setDuplicateAcknowledgments(without);
    setNumberConflicts(without);
    setManualNumbers(without);
    validatedInputs.current.delete(orderId);
  };

  const idempotencyKeyForSubmit = (order, outbox, retryMode) => {
    const base = outbox.idempotency_key || order.desktop_sync?.idempotency_key;
    if (!retryMode) return base;
    return `${base}:renumber:${retryMode}`.slice(0, 200);
  };

  const onValidate = order => act(order, {
    key: `order:${order.id}`, read: config => validateManualOrder(validationPayload(order), config),
    start: () => setValidatingId(order.id), finish: () => setValidatingId(null),
    accept: result => {
      validatedInputs.current.set(order.id, JSON.stringify(validationPayload(order)));
      setValidations(prev => ({ ...prev, [order.id]: result }));
      setDuplicateAcknowledgments(prev => ({ ...prev, [order.id]: false }));
    }, failure: 'Cloud validation failed',
  });

  const onDuplicateAcknowledgedChange = (orderId, checked) => {
    setDuplicateAcknowledgments(prev => ({ ...prev, [orderId]: checked }));
  };

  const onManualNumberChange = (orderId, value) => {
    setManualNumbers(prev => ({ ...prev, [orderId]: value }));
  };

  const onSubmit = async (item, options = {}) => {
    const order = item.order;
    const outbox = item.outbox || {};
    const validation = validations[order.id];
    if (!validation?.submit_ready || validatedInputs.current.get(order.id) !== JSON.stringify(validationPayload(order))) {
      toast.error('Validate this order before submitting it.');
      return;
    }
    if ((validation.duplicates?.length || 0) > 0 && !duplicateAcknowledgments[order.id]) {
      toast.error('Review and acknowledge possible duplicates before submitting.');
      return;
    }

    return act(item, {
      key: `order:${order.id}`, start: () => setSubmittingId(order.id), finish: () => setSubmittingId(null),
      cloud: config => createOrder(validationPayload(order, { internalOrderNumber: options.internalOrderNumber }),
        idempotencyKeyForSubmit(order, outbox, options.retryMode), config),
      validateCloud: value => typeof value?.id === 'string' && !!value.id,
      validateLocal: value => value?.reconciled === true && value.local_order_id === order.id && typeof value.cloud_order_id === 'string',
      local: (config, cloudOrder) => completeLocalReconciliation(order.id, cloudOrder, config),
      accept: result => {
        setCompleted(prev => [{ local: result.local_internal_order_number || result.temporary_label
          || order.internal_order_number || order.desktop_sync?.temporary_label || 'Offline order',
          cloud: result.cloud_internal_order_number || result.cloud_order_id }, ...prev]);
        forgetOrder(order.id);
      },
      reject: error => {
        if (error?.response?.data?.detail !== 'Internal order number already exists') return false;
        const number = options.internalOrderNumber || order.internal_order_number || order.desktop_sync?.provisional_internal_order_number;
        setNumberConflicts(prev => ({ ...prev, [order.id]: { number } }));
        setManualNumbers(prev => ({ ...prev, [order.id]: prev[order.id] || number || '' }));
        toast.error('Order number is already used online'); return true;
      },
      reason: 'offline_order_submitted', message: 'Offline order submitted to the cloud', failure: 'Failed to submit offline order',
    });
  };

  const onDiscard = item => {
    const order = item.order || {};
    const sync = order.desktop_sync || {};
    const label = order.internal_order_number || sync.provisional_internal_order_number || sync.temporary_label || order.recipient_name || 'this offline order';
    if (!window.confirm(`Discard ${label}? This removes the local offline order from this computer and it will not sync to the cloud.`)) return false;
    return act(item, {
      key: `order:${order.id}`, start: () => setDiscardingId(order.id), finish: () => setDiscardingId(null),
      local: config => discardLocalReconciliationOrder(order.id, config), accept: () => forgetOrder(order.id),
      reason: 'offline_order_discarded', message: 'Offline order discarded', failure: 'Failed to discard offline order',
    });
  };

  return {
    items,
    setItems,
    fetchItems,
    completed,
    validations,
    numberConflicts,
    manualNumbers,
    duplicateAcknowledgments,
    validatingId,
    submittingId,
    discardingId,
    onValidate,
    onDuplicateAcknowledgedChange,
    onManualNumberChange,
    onSubmit,
    onDiscard,
  };
}

export default useOfflineOrderReview;
